import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, BookOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, fmtDate, fmtNum } from "@/lib/api";
import { PageHeader, ScopeBadge } from "@/components/common";
import { useDebounced } from "@/components/DataTable";

function Field({ label, children, testid }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <div className="mt-1 text-sm text-slate-800" data-testid={testid}>{children}</div>
    </div>
  );
}

export default function CodeLookup() {
  const [code, setCode] = useState("");
  const [res, setRes] = useState(null);
  const [ms, setMs] = useState(null);
  const dc = useDebounced(code, 200);
  const seq = useRef(0);
  useEffect(() => {
    if (!dc.trim()) { setRes(null); return; }
    const n = ++seq.current;
    const t = performance.now();
    api.get("/annex/lookup", { params: { code: dc } }).then((r) => { if (n === seq.current) { setRes(r.data); setMs(Math.round(performance.now() - t)); } });
  }, [dc]);
  return (
    <div data-testid="code-lookup-page" className="max-w-3xl">
      <PageHeader title="Commodity code lookup" subtitle="Enter any commodity / HS code (4–10 digits; spaces and dots are ignored) to see how Annex I treats it and how many of our materials use it." />
      <div className="relative">
        <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
        <Input autoFocus data-testid="code-lookup-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. 3401 20 90 00"
          className="h-14 rounded-xl bg-white pl-12 font-mono text-lg" />
      </div>
      {res && (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="code-lookup-result">
          {!res.valid ? (
            <p className="text-sm font-medium text-rose-600" data-testid="code-lookup-message">{res.message}</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono text-lg font-semibold text-slate-900">{res.code}</span>
                <ScopeBadge scope={res.scope} from={res.applies_from} testid="code-lookup-scope" />
                {res.matched && res.scope === "future" && res.annex_scope === "check" && <ScopeBadge scope="check" testid="code-lookup-annex-scope" />}
              </div>
              {!res.matched ? (
                <p className="mt-3 text-sm text-slate-600" data-testid="code-lookup-message">{res.message}</p>
              ) : (
                <div className="mt-5 grid grid-cols-2 gap-5">
                  <Field label="Matched Annex I row" testid="code-lookup-row"><span className="font-mono font-semibold">{res.hs_code}</span> — {res.description}</Field>
                  <Field label="Commodity" testid="code-lookup-commodity">{res.commodity}</Field>
                  <Field label="Condition" testid="code-lookup-condition">{res.condition || "—"}</Field>
                  <Field label="Applies from" testid="code-lookup-applies-from">{fmtDate(res.applies_from)}</Field>
                  <Field label="Source" testid="code-lookup-source"><span className="text-xs text-slate-600">{res.source}</span></Field>
                </div>
              )}
              <div className="mt-5 border-t border-slate-100 pt-4">
                <Field label="Our materials with this code" testid="code-lookup-materials">
                  <span className="font-semibold">{fmtNum(res.materials.total)}</span> total · <span className="font-semibold">{fmtNum(res.materials.in_stock)}</span> in stock
                  {res.materials.total > 0 && <Link className="ml-3 text-xs text-[#D52B3B] underline-offset-2 hover:underline" to={`/materials?q=${res.code}`} data-testid="code-lookup-open-materials">Open in Materials</Link>}
                </Field>
              </div>
            </>
          )}
          {ms !== null && <p className="mt-4 text-[11px] text-slate-400" data-testid="code-lookup-ms">Looked up in {ms} ms</p>}
        </div>
      )}
      <Link to="/annex" className="mt-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="code-lookup-annex-link"><BookOpen className="h-4 w-4" />Annex I codes</Link>
    </div>
  );
}
